import { Portal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Event, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { GlobalSlideInService } from '@app/_shared/global-slide-in/services/global-slide-in.service';
import { DataObject } from '@app/_shared/interfaces';
import { FeatureFlag } from '@app/_shared/interfaces/http/companies/feature-flag';
import { AccountingGroup, RouteMappingService } from '@app/_shared/service/route-mapping.service';
import { companyFeatureFlags } from '@app/main-store/feature-flags.selectors';
import { Store } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'sb-main-layout',
  templateUrl: './main-layout.component.html',
  standalone: false, // eslint-disable-line
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'tw-flex tw-flex-col tw-bg-surface-50 dark:tw-bg-surface-950 tw-h-screen tw-w-full',
  },
})
export class MainLayoutComponent implements OnInit {
  private router = inject(Router);
  private slideInService = inject(GlobalSlideInService);
  private rxStore = inject(Store);
  private destroyRef = inject(DestroyRef);
  private routeMapping = inject(RouteMappingService);

  public activeRoute = signal<string>('');
  public activeRouteGroup = signal<'home' | 'accounting' | 'settings' | 'account' | 'toolbox' | null>('accounting');
  public activeAccountingGroup = signal<AccountingGroup>('orders');

  public slideInPortal: Portal<any>;
  public slideInWidth = '30rem';
  public slideInBackdrop = false;
  public slideInAutoFocus = false;
  public sidebarVisible = false;
  public tabGroupItems = [];
  public tabMenuItems: MenuItem[] = [];
  public activeTabMenuItem: MenuItem;
  public featureFlags: DataObject<FeatureFlag>[] = [];

  ngOnInit() {
    this.setupSlideInService();

    this.activeRoute.set(this.routeMapping.removeQueryParameters(this.router.url));
    this.updateActiveGroups(this.router.url);

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event: Event | RouterEvent) => event instanceof NavigationEnd),
      )
      .subscribe((event: NavigationEnd) => {
        this.activeRoute.set(this.routeMapping.removeQueryParameters(event.urlAfterRedirects));
        this.updateActiveGroups(event.urlAfterRedirects);
      });

    this.rxStore
      .select(companyFeatureFlags)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((flags) => (this.featureFlags = flags));
  }

  public onCloseSlideIn() {
    this.slideInService.close();
  }

  public setActiveRoute(route: string) {
    this.activeRoute.set(route);
    this.updateActiveTabMenuItem();
  }

  public isActiveRoute(route: string) {
    return this.activeRoute() === route;
  }

  private setupSlideInService() {
    this.slideInService.currentPortal$
      .pipe(
        filter(({ portal }) => portal !== null),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ portal, width, backdrop, autoFocus }) => {
        this.slideInPortal = portal;
        this.slideInWidth = width ?? '30rem';
        this.slideInBackdrop = backdrop;
        this.slideInAutoFocus = autoFocus;
        this.sidebarVisible = true;
      });

    this.slideInService.close$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.sidebarVisible = false;
    });
  }

  private updateActiveGroups(url: string) {
    const routeGroup = this.routeMapping.getRouteGroup(url);
    this.activeRouteGroup.set(routeGroup);

    if (routeGroup === 'accounting') {
      const accountingGroup = this.routeMapping.getAccountingGroup(url);
      this.activeAccountingGroup.set(accountingGroup);

      this.tabGroupItems = this.routeMapping.getTabGroupItems(accountingGroup);

      this.tabMenuItems = this.tabGroupItems.map((item) => ({
        label: item.label,
        routerLink: [item.route],
        command: () => this.setActiveRoute(item.route),
      }));

      this.updateActiveTabMenuItem();
    }
  }

  private updateActiveTabMenuItem() {
    this.activeTabMenuItem = this.tabMenuItems.find((item) => this.isActiveRoute(item.routerLink[0]));
  }
}
